
export enum QuestionType {
  LIKERT = 'LIKERT',
  YES_NO = 'YES_NO',
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE'
}

export interface Option {
  label: string;
  value: number;
}

export interface Question {
  id: string;
  text: string;
  type: QuestionType;
  options?: Option[]; // For Likert/Multiple Choice
  dependsOn?: {
    questionId: string;
    value: number | number[]; // Value(s) required in the parent question to show this one
  };
}

export interface SubscaleDefinition {
  id: string;
  name: string;
  questionIds: string[]; // List of question IDs belonging to this subscale
  description?: string;
}

/** Defines when a test result should trigger a clinical alert. */
export interface AlertThreshold {
  /** Minimum score (total or item) that triggers the alert. */
  score: number;
  /** Comparison direction: gte (≥), lte (≤), eq (=). */
  condition: 'gte' | 'lte' | 'eq';
  severity: 'warning' | 'critical';
  /** Human-readable clinical message shown to the clinician. */
  message: string;
  /** If set, evaluates answers[questionId] instead of totalScore. */
  questionId?: string;
}

/**
 * Datos normativos poblacionales para un test.
 * Permiten calcular el percentil del paciente respecto a la población general.
 */
export interface NormativeData {
  /** Referencia bibliográfica: autores, año, descripción de muestra. */
  source: string;
  /** Media de la población general. */
  populationMean: number;
  /** Desviación típica de la población general. */
  populationSD: number;
  /** Normas separadas por género (opcional). */
  genderNorms?: {
    male:   { mean: number; sd: number };
    female: { mean: number; sd: number };
  };
}

export interface TestDefinition {
  id: string;
  code: string; // e.g., 'PHQ-9'
  name: string;
  category: string; // New field for clinical category
  description: string;
  questions: Question[];
  subscales?: SubscaleDefinition[]; // Optional: For tests like STAI or PANSS
  scoringAlgorithm?: string; // 'qids-sr16' for domain-max algorithm; default is simple sum
  excludeFromTotal?: string[]; // question IDs excluded from totalScore (e.g. prosocial subscale, contextual items)
  alertThresholds?: AlertThreshold[]; // Optional: clinical alert rules
  normativeData?: NormativeData;        // Optional: population reference norms
  interpretations: {
    min: number;
    max: number;
    label: string;
    color: string;
  }[];
}

/** A clinical alert triggered when a test result exceeds a threshold. */
export interface ClinicalAlert {
  id: string;
  clinicianId: string;
  patientId: string | null;
  testResultId: string;
  testCode: string;
  patientName: string;
  severity: 'warning' | 'critical';
  message: string;
  triggeredScore: number | null;
  triggeredAt: string;
  readAt: string | null;
  dismissedAt: string | null;
}

export interface Patient {
  id: string;
  userId?: string; // NEW: Reference to auth.users - for multi-tenancy
  name: string;
  dateOfBirth?: string;
  gender?: 'M' | 'F' | 'Other' | 'Prefer not to say';
  email?: string;
  phone?: string;
  medicalRecordNumber?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  archived: boolean;

  // Demographic
  educationLevel?: string;
  occupation?: string;
  maritalStatus?: string;
  socioeconomicContext?: string;
  livingArrangement?: string;

  // Psychiatric History
  previousDiagnoses?: string;
  previousTreatments?: string;
  psychiatricHospitalizations?: string;
  suicideAttempts?: string;

  // Medical History
  chronicConditions?: string;
  currentMedications?: string;
  headTraumaHistory?: string;
  substanceUse?: string;

  // Family History
  familyMentalHealthHistory?: string;
  familySuicideHistory?: string;

  // Current Consultation
  consultationReason?: string;
  symptomDuration?: string;
  recentStressors?: string;
  functionalImpact?: string;

  // Previous Evaluations
  previousTestResults?: string;
  previousReports?: string;
  formalDiagnoses?: string;

  // Clinical Observations
  collaborationLevel?: string;
  fatigueObservations?: string;
  spontaneousComments?: string;
  observableBehavior?: string;
}

// Evolutivo - Clinical evolution notes
export interface Evolutivo {
  id: string;
  userId: string;
  patientId: string;
  date: string;
  content: string;
  linkedTestResultId?: string; // Optional link to a test result
  analysisInstructions?: string; // Custom instructions for AI analysis
  aiAnalysis?: {
    text: string;
    timestamp: string;
    modelUsed: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface AIAnalysisEntry {
  id: string;
  timestamp: string;
  analysis: string;
  customPrompt?: string; // Optional custom instructions from clinician
  modelUsed?: string; // e.g., 'gemini-1.5-flash'
}

export interface TestResult {
  id: string;
  userId?: string; // NEW: Reference to auth.users - for multi-tenancy
  patientId: string; // NEW: Reference to Patient instead of patientName
  testId: string;
  testCode: string; // NEW: e.g., 'PHQ-9'
  testName: string; // NEW: Full name
  testCategory: string; // NEW: Category
  date: string;
  answers: Record<string, number>; // questionId -> value
  totalScore: number;
  subscores?: Record<string, number>; // subscaleId -> score
  severityLabel: string;
  aiAnalysis?: string; // Deprecated: kept for backward compatibility
  aiAnalysisHistory?: AIAnalysisEntry[]; // NEW: Multiple AI analyses for premium users

  /**
   * @deprecated Clinician notes are now stored in Patient.notes
   * This field is kept for backward compatibility only.
   * Read from Patient.notes instead. DO NOT edit directly.
   */
  clinicianNotes?: string;

  createdAt?: string; // NEW
  updatedAt?: string; // NEW

  // Deprecated but kept for backward compatibility with localStorage
  patientName?: string;
}

export enum UserRole {
  ADMIN = 'ADMIN',
  CLINICIAN = 'CLINICIAN',
  SUPERADMIN = 'SUPERADMIN'
}

// Interface for the clinician/user in the database
export interface Clinician {
  id: string;
  userId?: string; // NEW: Reference to auth.users - links clinician to auth account
  createdByAdminId?: string;
  name: string;
  email: string;
  role: UserRole;
  avatarUrl?: string;
  phone?: string;
  licenseNumber?: string;
  specialization?: string;
  clinicianType?: string;
  maxClinicians?: number; // Límite de clínicos que puede crear (solo para Admin)
  evolutivosEnabled?: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// Legacy User interface (keeping for backward compatibility)
export interface User {
  id: string;
  name: string;
  role: UserRole;
  avatarUrl: string;
}

export type ViewState =
  | 'DASHBOARD'
  | 'TEST_LIST'
  | 'HISTORY'
  | 'PATIENTS_LIST'
  | 'USERS_MANAGEMENT'
  | 'EVOLUTIVOS'
  | 'SUBSCRIPTION'
  | 'PROFILE'
  | 'ADMIN_DASHBOARD'
  | 'SUPERADMIN_DASHBOARD'
  | { type: 'PATIENT_PROFILE', patientId: string }
  | { type: 'TAKE_TEST', testId: string, patientId?: string }
  | { type: 'VIEW_REPORT', resultId: string }
  | { type: 'COMPARE_TESTS', test1Id: string, test2Id: string }
  | { type: 'EVOLUTIVO_DETAIL', evolutivoId: string }
  | { type: 'CREATE_EVOLUTIVO', patientId?: string }
  | 'BATTERIES'
  | { type: 'BATTERY_RUNNER', sessionId: string }
  | { type: 'BATTERY_REPORT', sessionId: string }
  | 'SETTINGS';

// ── Test Batteries ─────────────────────────────────────────────────────────────

export interface TestBattery {
  id: string;
  name: string;
  description?: string;
  category?: string;
  testIds: string[];
  isTemplate: boolean;
  createdBy?: string;
  createdAt: string;
}

export interface BatterySession {
  id: string;
  batteryId?: string;
  batteryName: string;
  patientId: string;
  clinicianId: string;
  testIds: string[];
  status: 'in_progress' | 'completed' | 'abandoned';
  resultIds: string[];
  currentTestIndex: number;
  startedAt: string;
  completedAt?: string;
}

// Subscription types
export type SubscriptionTier = 'free' | 'pro' | 'enterprise';
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing';

// Professional type (affects AI report generation)
export type ProfessionalType = 'psychologist' | 'psychiatrist';

// User subscription profile
export interface Profile {
  id: string;
  subscriptionTier: SubscriptionTier;
  testsLimitPerMonth: number; // 0 = unlimited (for pro/enterprise)
  professionalType: ProfessionalType; // Psychologist (no meds) or Psychiatrist (can discuss meds)
  clinicianLimit?: number;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  subscriptionStatus?: SubscriptionStatus;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  createdAt: string;
  updatedAt: string;
}

// Monthly usage tracking
export interface UsageTracking {
  id: string;
  userId: string;
  monthYear: string; // Format: 'YYYY-MM' (e.g., '2025-12')
  testsTaken: number;
  evolutivoAnalysesGenerated: number; // Number of AI-generated evolutivo analyses this month
  lastTestAt?: string;
  createdAt: string;
  updatedAt: string;
}
